import TopBar from '@/components/feature/TopBar';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import WhatsAppButton from '@/components/feature/WhatsAppButton';
import { technicalAdvantages, finecoPageFaqs, siteConfig } from '@/mocks/data';
import { useState } from 'react';

export default function FinecoPage() {
  const [openFaqId, setOpenFaqId] = useState<number | null>(null);
  const whatsappHref = `https://wa.me/${siteConfig.whatsappNumber}?text=${encodeURIComponent('Merhaba, Fineco Isı Pompası hakkında detaylı bilgi almak istiyorum.')}`;

  return (
    <div className="min-h-screen bg-background-50">
      <TopBar />
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative w-full min-h-[450px] flex items-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Dark%20anthracite%20gray%20Fineco%20heat%20pump%20outdoor%20unit%20on%20modern%20villa%20wall%2C%20professional%20HVAC%20installation%2C%20energy%20efficiency%20concept%2C%20clean%20minimalist%20architecture%2C%20warm%20natural%20lighting%2C%20premium%20product%20photography%2C%20technical%20aesthetic&width=1600&height=700&seq=fineco-hero-2026&orientation=landscape"
              alt="Fineco Isı Pompası"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/35 to-black/60"></div>
          </div>
          <div className="relative z-10 w-full max-w-[1400px] mx-auto px-4 md:px-6 py-16 text-center">
            <h1 className="font-heading text-2xl md:text-5xl text-background-50 mb-4">Fineco Isı Pompası Sistemleri</h1>
            <p className="text-background-50/80 text-sm md:text-base max-w-3xl mx-auto leading-relaxed mb-6">
              Atak Sistem Enerji güvencesiyle Fineco Isı Pompaları; rezistans takviyesiz yapısı, hava kaynaklı çalışma prensibi, Mitsubishi kompresör teknolojisi ve A+++ verimliliği ile modern yaşam alanları için güçlü bir ısıtma ve soğutma çözümü sunar.
            </p>
            <a href={whatsappHref} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#25D366] text-white px-6 py-3 rounded-md text-sm font-semibold hover:bg-[#1faa51] transition-colors whitespace-nowrap">
              <i className="ri-whatsapp-line w-5 h-5 flex items-center justify-center"></i>
              WhatsApp'tan Bilgi Al
            </a>
          </div>
        </section>

        {/* Nedir? */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-6 text-center">Fineco Isı Pompası Nedir?</h2>
            <div className="max-w-3xl mx-auto text-foreground-600 text-sm md:text-base leading-relaxed space-y-4">
              <p>Fineco Isı Pompası; havadaki enerjiyi kullanarak ısıtma ve soğutma sağlayan, rezistans takviyesine ihtiyaç duymayan modern bir iklimlendirme sistemidir. %75 hava kaynaklı çalışma prensibi sayesinde dış ortam havasındaki ısı enerjisini verimli bir şekilde yaşam alanınıza aktarır.</p>
              <p>Geleneksel ısıtma sistemlerinden farklı olarak; kömür, doğalgaz veya elektrikli rezistans desteğine ihtiyaç duymaz. Mitsubishi kompresör teknolojisi ve bakır iç donanımı ile uzun ömürlü, güvenilir ve yüksek performanslı bir kullanım sunar.</p>
            </div>
            <div className="flex justify-center mt-8">
              <a href={whatsappHref} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-primary-500 text-background-50 px-5 py-2.5 rounded-md text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap">
                <i className="ri-whatsapp-line w-4 h-4 flex items-center justify-center"></i>
                Detaylı Bilgi Al
              </a>
            </div>
          </div>
        </section>

        {/* Teknik Avantajlar */}
        <section className="py-16 md:py-20 bg-background-100">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-10 text-center">Öne Çıkan Teknik Avantajlar</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 max-w-4xl mx-auto">
              {technicalAdvantages.map((adv, i) => (
                <div key={i} className="flex items-center gap-3 bg-background-50 rounded-lg p-4 border border-background-200/70">
                  <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center shrink-0">
                    <i className="ri-check-line text-primary-600 w-4 h-4 flex items-center justify-center"></i>
                  </div>
                  <span className="text-sm text-foreground-700 font-medium">{adv}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Hangi Alanlarda */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-6 text-center">Hangi Alanlarda Kullanılır?</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto">
              {['Müstakil Evler', 'Villalar', 'İş Yerleri', 'Geniş Yaşam Alanları'].map((area, i) => (
                <div key={i} className="bg-background-100 rounded-lg p-5 border border-background-200/70 text-center">
                  <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-3">
                    <i className={`text-primary-600 text-lg w-5 h-5 flex items-center justify-center ${i === 0 ? 'ri-home-4-line' : i === 1 ? 'ri-building-4-line' : i === 2 ? 'ri-store-2-line' : 'ri-aspect-ratio-line'}`}></i>
                  </div>
                  <span className="text-sm text-foreground-700 font-medium">{area}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Neden Atak Sistem */}
        <section className="py-16 md:py-20 bg-secondary-800">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 text-center">
            <h2 className="font-heading text-2xl md:text-3xl text-background-50 mb-6">Neden Atak Sistem Enerji?</h2>
            <p className="text-background-50/70 text-sm md:text-base max-w-2xl mx-auto leading-relaxed mb-8">
              Fineco Isı Pompaları Yetkili Bayisi olarak Balıkesir ve bölge illerde profesyonel yönlendirme, doğru kapasite seçimi ve kurulum desteği sunuyoruz. Yaşam alanınıza en uygun sistemi belirlemek için deneyimli ekibimizle yanınızdayız.
            </p>
            <a href={whatsappHref} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#25D366] text-white px-6 py-3 rounded-md text-sm font-semibold hover:bg-[#1faa51] transition-colors whitespace-nowrap">
              <i className="ri-whatsapp-line w-5 h-5 flex items-center justify-center"></i>
              Size Uygun Sistemi Öğrenin
            </a>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-10 text-center">Sık Sorulan Sorular</h2>
            <div className="max-w-3xl mx-auto flex flex-col gap-3">
              {finecoPageFaqs.map((faq) => (
                <div key={faq.id} className="bg-background-100 rounded-lg border border-background-200/70 overflow-hidden">
                  <button
                    onClick={() => setOpenFaqId(openFaqId === faq.id ? null : faq.id)}
                    className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-background-200/50 transition-colors cursor-pointer"
                  >
                    <span className="text-sm md:text-base font-medium text-foreground-900 pr-4">{faq.question}</span>
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 transition-colors ${openFaqId === faq.id ? 'bg-primary-500 text-background-50' : 'bg-background-300 text-foreground-500'}`}>
                      <i className={`text-sm w-4 h-4 flex items-center justify-center ${openFaqId === faq.id ? 'ri-subtract-line' : 'ri-add-line'}`}></i>
                    </div>
                  </button>
                  {openFaqId === faq.id && (
                    <div className="px-5 pb-4">
                      <p className="text-foreground-600 text-sm leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-16 bg-background-100">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 text-center">
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-4">Fineco Isı Pompası Hakkında Bilgi Alın</h2>
            <p className="text-foreground-600 text-sm max-w-xl mx-auto mb-6">Size uygun sistemi belirlemek için hemen WhatsApp üzerinden bize ulaşın.</p>
            <a href={whatsappHref} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#25D366] text-white px-6 py-3 rounded-md text-sm font-semibold hover:bg-[#1faa51] transition-colors whitespace-nowrap">
              <i className="ri-whatsapp-line w-5 h-5 flex items-center justify-center"></i>
              WhatsApp'tan Bilgi Al
            </a>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppButton />
    </div>
  );
}